import { useEffect, useState } from "react";
// REDUX
import { useGetRolesLightQuery } from "../../redux/roles/roleAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACE
import { RoleLightInterface } from "../../interfaces/Roles.interface";

/**
 * Fetches the roles of one organisation via `useGetRolesLightQuery`.
 *
 * Resolves a JWT first and passes it to the query, which stays skipped until the
 * token arrives. Scoped to the globally selected organisation unless overridden.
 */
export default function useGetAllRolesLight({ orgId }: { orgId: string }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [roles, setRoles] = useState<RoleLightInterface[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [errorObject, setErrorObject] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>("Error");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  // `orgId` is empty while a caller (e.g. the header's org+role switcher)
  // hasn't picked an organization yet — skip rather than hit
  // `/roles/light/` with a blank id.
  const { data, isLoading, isError, isFetching, error } = useGetRolesLightQuery(
    { token, orgId },
    { skip: !token || !orgId, refetchOnMountOrArgChange: false },
  );

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setRoles(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorObject(error);
    }
  }, [error]);

  useEffect(() => {
    if (errorObject && errorObject.data && errorObject.data.message) {
      setErrorMessage(errorObject.data.message);
    }
  }, [errorObject]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return {
    rolesLight: roles,
    isLoadingGetAllRolesLight: isLoading || isFetching,
    errorMessageGetAllRolesLight: errorMessage,
    isErrorGetAllRolesLight: isError,
    totalRowsGetAllRolesLight: data?.total ?? 0,
  };
}
